<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { PanelLeftOpen, PanelLeftClose } from 'lucide-vue-next';
import { useColorScheme } from '@/assets/js/functions/useColorScheme.js';
import PanelContent from './PanelContent.vue';

import { useAppStore } from '@/store/app/app.js';

const appStore = useAppStore();
const { prefersDark } = useColorScheme();

const isOpen = ref(false);
const hoverTimeout = ref(null);

const PANEL_WIDTH = '20rem';

/** Panel mode: 'hover' = show on hover, 'fixed' = always visible, push content */
const panelMode = computed(() => {
  const s = appStore.state.uixpress_settings;
  if (s?.floating_panel_mode) return s.floating_panel_mode;
  return s?.floating_panel_permanent ? 'fixed' : 'hover';
});

const isFixed = computed(() => panelMode.value === 'fixed');
const isHover = computed(() => panelMode.value === 'hover');

/**
 * Checks whether the current user should see the panel based on
 * floating_panel_roles. An empty list means all users see it.
 */
const canSeePanel = computed(() => {
  const roles = appStore.state.uixpress_settings?.floating_panel_roles;
  if (!Array.isArray(roles) || roles.length === 0) return true;

  const userRoles = appStore.state.userRoles || [];
  const userId = String(appStore.state.userID || '');

  return roles.some((item) => {
    if (item.type === 'role') return userRoles.includes(item.value);
    if (item.type === 'user') return String(item.id) === userId;
    return false;
  });
});

const close = () => {
  if (!isFixed.value) isOpen.value = false;
};

const handleHoverEnter = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  isOpen.value = true;
};

const handleHoverLeave = () => {
  if (hoverTimeout.value) clearTimeout(hoverTimeout.value);
  hoverTimeout.value = setTimeout(() => {
    isOpen.value = false;
    hoverTimeout.value = null;
  }, 150);
};

const applyFixedMode = (fixed) => {
  if (fixed) {
    isOpen.value = true;
    document.body.style.transition =
      'margin-left 280ms cubic-bezier(0.32, 0.72, 0, 1)';
    document.body.style.marginLeft = PANEL_WIDTH;
  } else {
    document.body.style.marginLeft = '';
    document.body.style.transition = '';
  }
};

watch(isFixed, applyFixedMode, { immediate: true });

const onKeydown = (e) => {
  if (e.key === 'Escape' && !isFixed.value) isOpen.value = false;
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
  if (hoverTimeout.value) clearTimeout(hoverTimeout.value);
  document.body.style.marginLeft = '';
  document.body.style.transition = '';
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="canSeePanel"
      :class="prefersDark ? 'dark' : ''"
      class="uixpress-isolation"
      style="--spacing: 3.5px"
    >
      <!-- Hover mode: overlay only when panel is open (not fixed) -->
      <Transition
        enter-active-class="transition-opacity duration-200 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen && isHover"
          class="fixed inset-0 z-[99997] cursor-pointer bg-black/40"
          @click="close"
        />
      </Transition>

      <!-- Hover mode: trigger + panel in one container for hover continuity -->
      <div
        v-if="isHover"
        class="fixed bottom-6 left-6 z-[99998] flex items-end"
        @mouseenter="handleHoverEnter"
        @mouseleave="handleHoverLeave"
      >
        <!-- Trigger (Floating Action Button) -->
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#141414] text-zinc-200 shadow-lg shadow-black/30 backdrop-blur-sm transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <img
            v-if="appStore.state?.uixpress_settings?.logo"
            :src="
              prefersDark && appStore.state?.uixpress_settings?.dark_logo
                ? appStore.state.uixpress_settings.dark_logo
                : appStore.state.uixpress_settings.logo
            "
            class="h-6 w-6 object-contain"
            alt=""
          />
          <div
            v-else
            class="relative h-5 w-5 shrink-0"
          >
            <PanelLeftOpen
              :size="20"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-zinc-400 transition-opacity duration-200"
              :class="isOpen ? 'opacity-0' : 'opacity-100'"
            />
            <PanelLeftClose
              :size="20"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-zinc-400 transition-opacity duration-200"
              :class="isOpen ? 'opacity-100' : 'opacity-0'"
            />
          </div>
        </div>

        <!-- Panel (Floating, expands from trigger) -->
        <Transition
          enter-active-class="transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 scale-90 -translate-x-4 translate-y-4"
          leave-to-class="opacity-0 scale-90 -translate-x-4 translate-y-4"
        >
          <div
            v-show="isOpen"
            class="ml-4 flex h-[80vh] max-h-[800px] w-80 origin-bottom-left flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]/98 shadow-2xl shadow-black/60 backdrop-blur-md"
          >
            <PanelContent :show-close="true" @close="close" />
          </div>
        </Transition>
      </div>

      <!-- Fixed mode: panel always visible, push content, no overlay -->
      <Transition
        v-if="isFixed"
        enter-active-class="transition-transform duration-280 ease-[cubic-bezier(0.32,0.72,0,1)]"
        leave-active-class="transition-transform duration-220 ease-in"
        enter-from-class="-translate-x-full"
        leave-to-class="-translate-x-full"
      >
        <div
          v-show="isFixed"
          class="fixed inset-y-0 left-0 z-[99998] flex w-80 flex-col overflow-hidden border-r border-white/10 bg-[#0a0a0a]"
        >
          <PanelContent :show-close="false" />
        </div>
      </Transition>
    </div>
  </Teleport>
</template>
